import React, { useEffect, lazy } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import Login from './pages/Login';
import HomePage from './pages/HomePage';
import Layout from './components/Layout';

/**
 * Pages are code-split: each becomes its own chunk loaded on navigation, so the
 * initial download is the shell plus whatever the first route needs rather than
 * all 22 pages at once. Login and Layout stay eager — they are the first paint
 * and the frame around every page, so they belong in the main bundle.
 *
 * The <Suspense> that shows a fallback while a chunk loads lives around the
 * <Outlet> inside Layout, so the sidebar stays on screen during the load.
 */
const Leads = lazy(() => import('./pages/Leads'));
const Users = lazy(() => import('./pages/Users'));
const SuperAdminOrgs = lazy(() => import('./pages/SuperAdminOrgs'));
const ServerHealth = lazy(() => import('./pages/ServerHealth'));
const ApiCallLogs = lazy(() => import('./pages/ApiCallLogs'));
const IntegrationCredentials = lazy(() => import('./pages/IntegrationCredentials'));
const RTM = lazy(() => import('./pages/RTM'));
const Reports = lazy(() => import('./pages/Reports'));
const AgentLeads = lazy(() => import('./pages/AgentLeads'));
const AdvisorDashboard = lazy(() => import('./pages/AdvisorDashboard'));
const AgentSummary = lazy(() => import('./pages/AgentSummary'));
const MySummary = lazy(() => import('./pages/MySummary'));
const PriorityConfig = lazy(() => import('./pages/PriorityConfig'));
const WebphoneConfig = lazy(() => import('./pages/WebphoneConfig'));
const QueueManagement = lazy(() => import('./pages/QueueManagement'));
const QueueAllocationByUser = lazy(() => import('./pages/QueueAllocationByUser'));
const FileUpload = lazy(() => import('./pages/FileUpload'));
const AdminLeadDetails = lazy(() => import('./pages/AdminLeadDetails'));
const IPManagement = lazy(() => import('./pages/IPManagement'));
const UMS = lazy(() => import('./pages/UMS'));
const TeamDashboard = lazy(() => import('./pages/TeamDashboard'));
const Guide = lazy(() => import('./pages/Guide'));
const CallRatings = lazy(() => import('./pages/CallRatings'));
import { requestAllPermissions } from './utils/browserPermissions';
import { initGA, trackPageView } from './utils/analytics';
import { getDefaultPath, hasPermission, canMakeCalls, isAdmin, isManager, isTL, isAdvisor, isSupervisory, isSuperAdmin } from './utils/roles';
import toast from 'react-hot-toast';

const ProtectedRoute = ({ children }) => {
  const { isAuthenticated } = useSelector((state) => state.auth);
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  return children;
};

/** Superadmin-only. The flag is separate from userRole: an org admin is not a superadmin. */
const SuperAdminRoute = ({ children }) => {
  const { user, isAuthenticated } = useSelector((state) => state.auth);
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  if (!isSuperAdmin(user)) return <Navigate to={getDefaultPath(user)} replace />;
  return children;
};

const RoleRoute = ({ roles, permission, children }) => {
  const { user, isAuthenticated } = useSelector((state) => state.auth);
  if (!isAuthenticated) return <Navigate to="/login" replace />;

  if (permission && !hasPermission(user, permission)) {
    return <Navigate to={getDefaultPath(user)} replace />;
  }

  if (roles) {
    const userRole = user?.userRole || (user?.userIsAdmin ? 'admin' : 'advisor');
    if (!roles.includes(userRole)) {
      return <Navigate to={getDefaultPath(user)} replace />;
    }
  }

  return children;
};

const DefaultRedirect = () => {
  const { user, isAuthenticated } = useSelector((state) => state.auth);
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  return <Navigate to={getDefaultPath(user)} replace />;
};

/**
 * The public front door at "/". A logged-out visitor sees the marketing landing;
 * a signed-in user is sent straight to their dashboard, so the homepage never
 * gets in the way of people who already work here.
 */
const RootGate = () => {
  const { user, isAuthenticated } = useSelector((state) => state.auth);
  if (isAuthenticated) return <Navigate to={getDefaultPath(user)} replace />;
  return <HomePage />;
};

function App() {
  const { isAuthenticated, user } = useSelector((state) => state.auth);
  const location = useLocation();

  useEffect(() => {
    initGA();
  }, []);

  useEffect(() => {
    trackPageView(location.pathname + location.search);
  }, [location]);

  // Client-side navigation keeps the previous scroll offset; start each page at the top.
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  useEffect(() => {
    if (isAuthenticated && user) {
      requestAllPermissions().then((results) => {
        if (results.microphone === 'granted') {
          console.log('Microphone permission granted');
        } else if (results.microphone === 'denied') {
          toast.error('Microphone permission denied. Please enable it in browser settings for calling features.');
        }
      }).catch((err) => {
        console.error('Error requesting permissions:', err);
      });
    }
  }, [isAuthenticated, user]);

  const defaultPath = isAuthenticated ? getDefaultPath(user) : "/login";

  return (
    <Routes>
      <Route
        path="/login"
        element={isAuthenticated ? <Navigate to={defaultPath} replace /> : <Login />}
      />

      {/* Public landing at "/" for logged-out visitors; signed-in users are
          redirected to their dashboard by RootGate. */}
      <Route path="/" element={<RootGate />} />

      {/* Authenticated app shell. Pathless so each child keeps its absolute path
          (/leads, /users, …) while "/" stays the public landing above. */}
      <Route
        element={
          <ProtectedRoute>
            <Layout />
          </ProtectedRoute>
        }
      >
        <Route path="dashboard" element={<DefaultRedirect />} />

        {/* Permission-gated pages */}
        <Route path="leads" element={<RoleRoute permission="leads.view"><Leads /></RoleRoute>} />
        <Route path="leads/:leadId" element={<RoleRoute permission="leads.view"><AdminLeadDetails /></RoleRoute>} />
        <Route path="users" element={<RoleRoute permission="users.view"><Users /></RoleRoute>} />
        <Route path="organisations" element={<SuperAdminRoute><SuperAdminOrgs /></SuperAdminRoute>} />
        <Route path="api-keys" element={<SuperAdminRoute><IntegrationCredentials /></SuperAdminRoute>} />
        <Route path="server-health" element={<SuperAdminRoute><ServerHealth /></SuperAdminRoute>} />
        <Route path="api-logs" element={<SuperAdminRoute><ApiCallLogs /></SuperAdminRoute>} />
        <Route path="queue-management" element={<RoleRoute permission="queueManagement"><QueueManagement /></RoleRoute>} />
        <Route path="queue-management/queues/:queueId/allocation-by-user" element={<RoleRoute permission="queueManagement"><QueueAllocationByUser /></RoleRoute>} />
        <Route path="file-upload" element={<RoleRoute permission="bulkUpload"><FileUpload /></RoleRoute>} />
        <Route path="lms" element={<RoleRoute permission="lms"><UMS mode="lms" /></RoleRoute>} />
        <Route path="ums" element={<RoleRoute permission="ums"><UMS mode="ums" /></RoleRoute>} />
        <Route path="priority-config" element={<RoleRoute permission="priorityConfig"><PriorityConfig /></RoleRoute>} />
        <Route path="webphone-config" element={<RoleRoute permission="webphoneConfig"><WebphoneConfig /></RoleRoute>} />
        <Route path="ip-management" element={<RoleRoute permission="ipManagement"><IPManagement /></RoleRoute>} />
        <Route path="rtm" element={<RoleRoute permission="rtm"><RTM /></RoleRoute>} />
        <Route path="reports" element={<RoleRoute permission="reports"><Reports /></RoleRoute>} />
        <Route path="call-ratings" element={<RoleRoute permission="callRatings"><CallRatings /></RoleRoute>} />

        {/* Manager / TL team dashboard */}
        <Route path="team" element={<RoleRoute permission="team.tabVisibility"><TeamDashboard /></RoleRoute>} />

        {/* Guide — accessible to all authenticated users */}
        <Route path="guide" element={<Guide />} />

        {/* Lead detail — accessible by anyone logged in (backend scopes data) */}
        <Route path="lead-detail/:leadId" element={<AdminLeadDetails />} />

        {/* Advisor-only routes */}
        <Route path="my-leads" element={<RoleRoute roles={['advisor']}><AdvisorDashboard /></RoleRoute>} />
        <Route path="my-leads-old" element={<RoleRoute roles={['advisor']}><AgentLeads /></RoleRoute>} />
        <Route path="my-summary" element={<RoleRoute roles={['advisor']}><MySummary /></RoleRoute>} />
        <Route path="my-performance" element={<RoleRoute roles={['advisor']}><AgentSummary /></RoleRoute>} />
      </Route>

      <Route path="*" element={
        isAuthenticated
          ? <Navigate to={defaultPath} replace />
          : <Navigate to="/login" replace />
      } />
    </Routes>
  );
}

export default App;
